import React from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";

import {
  AlertTriangle,
  ArrowLeft,
  Archive,
  BadgeCheck,
  Banknote,
  Building2,
  Check,
  ChevronRight,
  CircleDollarSign,
  CircleOff,
  Clock3,
  Database,
  FileText,
  FolderKanban,
  Globe2,
  Landmark,
  Loader2,
  MailWarning,
  MapPin,
  MessageSquareText,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Toolbox,
  UserRound,
  UsersRound,
  Wrench,
  X,
  Plus,
} from "lucide-react";

import "./AdminOrganisationDetailPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/* =========================================================
   HELPERS
   ========================================================= */

function useAnimatedNumber(targetValue, duration = 700) {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const target = Number(targetValue) || 0;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);

      return undefined;
    }

    let frame;
    let startedAt = null;

    function animate(timestamp) {
      if (!startedAt) {
        startedAt = timestamp;
      }

      const progress = Math.min((timestamp - startedAt) / duration, 1);

      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    }

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [targetValue, duration]);

  return value;
}

function AnimatedNumber({ value, locale = "en-GB" }) {
  const animated = useAnimatedNumber(value);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Math.round(animated));
}

function AnimatedCurrency({ value, locale = "en-GB" }) {
  const animated = useAnimatedNumber(value, 850);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(animated);
}

function getInitials(name) {
  if (!name) {
    return "—";
  }

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getUserAuthenticationMeta(user, t) {
  const status = user?.authentication?.status;

  if (status === "VERIFIED") {
    return {
      key: "verified",

      label: t("authentication.verified", "Verified"),

      icon: BadgeCheck,
    };
  }

  if (status === "UNVERIFIED") {
    return {
      key: "unverified",

      label: t("authentication.unverified", "Unverified"),

      icon: MailWarning,
    };
  }

  return {
    key: "local",

    label: t("authentication.localOnly", "Local only"),

    icon: Database,
  };
}

function formatRole(role, t) {
  if (!role) {
    return t("common.notSpecified", "Not specified");
  }

  const knownRoles = {
    ADMIN: t("roles.admin", "Administrator"),

    MANAGER: t("roles.manager", "Manager"),

    STAFF: t("roles.staff", "Staff"),

    LOAN_OFFICER: t("roles.loanOfficer", "Loan Officer"),

    UNDERWRITER: t("roles.underwriter", "Underwriter"),

    AUDITOR: t("roles.auditor", "Auditor"),
  };

  return knownRoles[role] || role;
}

function formatOrganisationType(type, t) {
  if (type === "BANK") {
    return t("types.lender", "Lender organisation");
  }

  if (type === "OSS") {
    return t("types.oss", "One-Stop Shop");
  }

  return type || t("common.notSpecified", "Not specified");
}

function formatCountry(code, t) {
  if (!code) {
    return "";
  }

  const fallback = {
    BE: "Belgium",
    ES: "Spain",
    PT: "Portugal",
    LU: "Luxembourg",
  };

  return t(`countries.${code}`, fallback[code] || code);
}

function formatRegion(region, t) {
  if (!region) {
    return "";
  }

  const fallback = {
    FLANDERS: "Flanders",
    GIRONA: "Girona",
  };

  return t(
    `regions.${region}`,
    fallback[region] ||
      region
        .toLowerCase()
        .replaceAll("_", " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
  );
}

function formatLocation(organisation, t) {
  const country = formatCountry(organisation?.country_code, t);

  const region = formatRegion(organisation?.region, t);

  return [country, region].filter(Boolean).join(" / ");
}

function formatDateTime(value, locale) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",

    timeStyle: "short",
  }).format(date);
}

function getActivityIcon(type) {
  switch (type) {
    case "USER":
      return UserRound;

    case "LOAN_CASE":
      return FileText;

    case "PROJECT":
      return FolderKanban;

    case "MESSAGE":
      return MessageSquareText;

    case "ORGANISATION":
      return Building2;

    default:
      return Clock3;
  }
}

function humaniseActivityValue(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normaliseActivityType(item) {
  const value = String(
    item?.type || item?.area || item?.target?.type || "",
  ).toUpperCase();

  if (value.includes("LOAN") || value.includes("DOSSIER")) {
    return "LOAN_CASE";
  }

  if (value.includes("PROJECT") || value.includes("RENOVATION")) {
    return "PROJECT";
  }

  if (
    value.includes("MESSAGE") ||
    value.includes("COMMUNICATION") ||
    value.includes("THREAD")
  ) {
    return "MESSAGE";
  }

  if (value.includes("USER") || value.includes("AUTH")) {
    return "USER";
  }

  return "ORGANISATION";
}

function getActivityTitle(item, t) {
  if (item?.title) {
    return item.title;
  }

  const operation = item?.metadata?.operation;

  if (operation) {
    return humaniseActivityValue(operation);
  }

  const targetType = item?.target?.type;

  const action = humaniseActivityValue(item?.action);

  if (targetType && action) {
    return `${humaniseActivityValue(targetType)} ${action.toLowerCase()}`;
  }

  return (
    action ||
    humaniseActivityValue(item?.area) ||
    t("recentActivity.activity", "Platform activity")
  );
}

function getActivityDescription(item) {
  if (item?.description) {
    return item.description;
  }

  const actorName = item?.actor?.name || item?.actor_name || "";

  const targetLabel = item?.target?.label || item?.target_label || "";

  const role = item?.acting_role || item?.actor?.role || "";

  const parts = [];

  if (actorName) {
    parts.push(actorName);
  }

  if (role) {
    parts.push(humaniseActivityValue(role));
  }

  if (targetLabel) {
    parts.push(targetLabel);
  }

  return parts.join(" · ");
}

function normaliseRegionIds(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return [
    ...new Set(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value)),
    ),
  ].sort((left, right) => left - right);
}

/* =========================================================
   METRIC
   ========================================================= */

function MetricCard({
  icon: Icon,
  label,
  description,
  value,
  currency = false,
  locale,
  delay = 0,
}) {
  return (
    <article
      className={"admin-organisation-detail-metric"}
      style={{
        "--metric-delay": `${delay}ms`,
      }}
    >
      <span className={"admin-organisation-detail-metric__icon"}>
        <Icon size={19} />
      </span>

      <div>
        <span>{label}</span>

        <strong>
          {currency ? (
            <AnimatedCurrency value={value} locale={locale} />
          ) : (
            <AnimatedNumber value={value} locale={locale} />
          )}
        </strong>

        {description ? <small>{description}</small> : null}
      </div>
    </article>
  );
}

/* =========================================================
   INFORMATION ROW
   ========================================================= */

function InformationRow({ label, value }) {
  return (
    <div className={"admin-organisation-detail-info-row"}>
      <span>{label}</span>

      <strong>{value || "—"}</strong>
    </div>
  );
}

/* =========================================================
   USER ROW
   ========================================================= */

function OrganisationUserRow({
  user,
  t,
  onOpen,
  onTooltipMove,
  onTooltipHide,
}) {
  const authMeta = getUserAuthenticationMeta(user, t);

  const AuthIcon = authMeta.icon;

  const userInitials =
    user.initials || getInitials(user.display_name || user.email);

  return (
    <article
      className={"admin-organisation-detail-user"}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(user.id)}
      onMouseMove={(event) =>
        onTooltipMove({
          userId: user.id,
          x: event.clientX,
          y: event.clientY,
        })
      }
      onMouseLeave={onTooltipHide}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          onOpen(user.id);
        }
      }}
    >
      <div className={"admin-organisation-detail-user__avatar"}>
        <span>{userInitials}</span>
      </div>

      <div className={"admin-organisation-detail-user__content"}>
        <div className={"admin-organisation-detail-user__name-line"}>
          <strong>{user.display_name || user.email}</strong>

          <span
            className={
              "admin-organisation-detail-" +
              "auth-badge " +
              "admin-organisation-detail-" +
              `auth-badge--${authMeta.key}`
            }
          >
            <AuthIcon size={12} />

            {authMeta.label}
          </span>

          <span
            className={`admin-organisation-detail-account-badge ${
              user.is_active ? "is-active" : "is-inactive"
            }`}
          >
            {user.is_active
              ? t("account.active", "Active")
              : t("account.inactive", "Inactive")}
          </span>
        </div>

        <span>{user.email}</span>

        {user.org_role ? <small>{formatRole(user.org_role, t)}</small> : null}
      </div>

      <ChevronRight
        size={17}
        className={"admin-organisation-detail-user__chevron"}
      />
    </article>
  );
}

/* =========================================================
   RECENT ACTIVITY
   ========================================================= */

function RecentActivity({ items, locale, t }) {
  const activityItems = React.useMemo(
    () =>
      (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        displayType: normaliseActivityType(item),
        displayTitle: getActivityTitle(item, t),
        displayDescription: getActivityDescription(item),
      })),
    [items, t],
  );

  return (
    <section
      className={
        "admin-organisation-detail-card " +
        "admin-organisation-detail-card--wide"
      }
    >
      <header className={"admin-organisation-detail-card__header"}>
        <div className={"admin-organisation-detail-card__title"}>
          <span className={"admin-organisation-detail-card__icon"}>
            <Clock3 size={18} />
          </span>

          <div>
            <h2>{t("recentActivity.title", "Recent Activity")}</h2>

            <p>
              {t(
                "recentActivity.subtitle",
                "Latest organisation-related platform activity.",
              )}
            </p>
          </div>
        </div>

        {activityItems.length > 0 ? (
          <span className={"admin-organisation-detail-activity-count"}>
            {t("recentActivity.events", "{{count}} recent events", {
              count: activityItems.length,
            })}
          </span>
        ) : null}
      </header>

      <div className={"admin-organisation-detail-card__body"}>
        {activityItems.length > 0 ? (
          <div className={"admin-organisation-detail-activity-list"}>
            {activityItems.map((item, index) => {
              const ActivityIcon = getActivityIcon(item.displayType);

              return (
                <article
                  key={item.id ?? `${item.displayType}-${index}`}
                  className={"admin-organisation-detail-activity"}
                  style={{
                    "--activity-delay": `${index * 55}ms`,
                  }}
                >
                  <span className={"admin-organisation-detail-activity__icon"}>
                    <ActivityIcon size={17} />
                  </span>

                  <div
                    className={"admin-organisation-detail-activity__content"}
                  >
                    <strong>{item.displayTitle}</strong>

                    {item.displayDescription ? (
                      <span>{item.displayDescription}</span>
                    ) : null}
                  </div>

                  <time dateTime={item.created_at || undefined}>
                    {formatDateTime(item.created_at, locale)}
                  </time>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={"admin-organisation-detail-activity-empty"}>
            <Clock3 size={25} />

            <strong>
              {t("recentActivity.emptyTitle", "No activity recorded yet")}
            </strong>

            <span>
              {t(
                "recentActivity.emptyText",
                "No audited organisation activity is available yet.",
              )}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function loadGoogleMaps(apiKey) {
  if (!apiKey || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise;
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const scriptId = "legup-google-maps-script";

    const existingScript = document.getElementById(scriptId);

    function resolveWhenReady() {
      if (window.google?.maps) {
        resolve(window.google);
      } else {
        reject(new Error("Google Maps API is unavailable."));
      }
    }

    if (existingScript) {
      if (window.google?.maps) {
        resolve(window.google);

        return;
      }

      existingScript.addEventListener("load", resolveWhenReady, {
        once: true,
      });

      existingScript.addEventListener("error", reject, {
        once: true,
      });

      return;
    }

    const script = document.createElement("script");

    script.id = scriptId;

    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(apiKey)}` +
      "&libraries=places&v=weekly";

    script.async = true;
    script.defer = true;

    script.onload = resolveWhenReady;

    script.onerror = () => {
      window.__legupGoogleMapsPromise = null;

      reject(new Error("Failed to load Google Maps."));
    };

    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

/* =========================================================
   PAGE
   ========================================================= */

export default function AdminOrganisationDetailPage() {
  const navigate = useNavigate();

  const { organisationId } = useParams();

  const { getAccessTokenSilently } = useAuth0();

  const { t, i18n } = useTranslation("admin-organisation-detail");

  const { showToast } = useToast();

  /* =====================================================
     ORGANISATION DATA
     ===================================================== */

  const [data, setData] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [error, setError] = React.useState("");

  /* =====================================================
     MARKET REGIONS
     ===================================================== */

  const [marketRegionsLoading, setMarketRegionsLoading] = React.useState(false);

  const [marketRegionsSaving, setMarketRegionsSaving] = React.useState(false);

  const [marketRegionsError, setMarketRegionsError] = React.useState("");

  const [availableMarketRegions, setAvailableMarketRegions] = React.useState(
    [],
  );

  const [selectedMarketRegionIds, setSelectedMarketRegionIds] = React.useState(
    [],
  );

  const [savedMarketRegionIds, setSavedMarketRegionIds] = React.useState([]);

  /* =====================================================
     ARCHIVE
     ===================================================== */

  const [archiving, setArchiving] = React.useState(false);

  const [archiveModalOpen, setArchiveModalOpen] = React.useState(false);

  /* =====================================================
     TOOLTIP
     ===================================================== */

  const [userTooltip, setUserTooltip] = React.useState({
    visible: false,

    userId: null,

    x: 0,

    y: 0,
  });

  /* =====================================================
     LOCALE
     ===================================================== */

  const locale =
    i18n.language === "nl"
      ? "nl-BE"
      : i18n.language === "es"
        ? "es-ES"
        : i18n.language === "ca"
          ? "ca-ES"
          : "en-GB";

  /* =====================================================
     EDIT MODE
     ===================================================== */

  const [editModalOpen, setEditModalOpen] = React.useState(false);

  const [editSaving, setEditSaving] = React.useState(false);

  const [editError, setEditError] = React.useState("");

  const [editForm, setEditForm] = React.useState({
    name: "",
    vat_number: "",
    postal_codes: [],
  });

  const [postalSearch, setPostalSearch] = React.useState("");
  const [postalSuggestions, setPostalSuggestions] = React.useState([]);
  const [postalSearching, setPostalSearching] = React.useState(false);
  const [postalSearchError, setPostalSearchError] = React.useState("");

  const postalSessionTokenRef = React.useRef(null);
  const postalSearchRequestRef = React.useRef(0);
  const postalSearchTimerRef = React.useRef(null);

  /* =====================================================
     LOAD ORGANISATION
     ===================================================== */

  const loadOrganisation = React.useCallback(async () => {
    try {
      setLoading(true);

      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}` + "/api/admin/" + "organisations/" + `${organisationId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.detail ||
            t("errors.load", "Could not load organisation details."),
        );
      }

      setData(payload);
    } catch (err) {
      console.error("Failed to load organisation:", err);

      setError(
        err.message || t("errors.load", "Could not load organisation details."),
      );
    } finally {
      setLoading(false);
    }
  }, [organisationId, getAccessTokenSilently, t]);

  React.useEffect(() => {
    loadOrganisation();
  }, [loadOrganisation]);

  /* =====================================================
     LOAD LENDER MARKET REGIONS
     ===================================================== */

  const loadMarketRegions = React.useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setMarketRegionsLoading(true);
        }

        setMarketRegionsError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}` +
            "/api/admin/" +
            "organisations/" +
            `${organisationId}/` +
            "market-regions/",
          {
            headers: {
              Authorization: `Bearer ${token}`,

              Accept: "application/json",
            },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload.detail ||
              t(
                "marketRegions.loadError",
                "Could not load participating markets.",
              ),
          );
        }

        const available = Array.isArray(payload.available_market_regions)
          ? payload.available_market_regions
          : [];

        const selected = normaliseRegionIds(payload.market_region_ids);

        setAvailableMarketRegions(available);

        setSelectedMarketRegionIds(selected);

        setSavedMarketRegionIds(selected);
      } catch (err) {
        setMarketRegionsError(
          err.message ||
            t(
              "marketRegions.loadError",
              "Could not load participating markets.",
            ),
        );
      } finally {
        if (!silent) {
          setMarketRegionsLoading(false);
        }
      }
    },
    [organisationId, getAccessTokenSilently, t],
  );

  React.useEffect(() => {
    if (data?.organisation?.org_type !== "BANK") {
      return;
    }

    loadMarketRegions();
  }, [data?.organisation?.org_type, loadMarketRegions]);

  /* =====================================================
     MARKET REGION DIRTY STATE
     ===================================================== */

  const marketRegionsDirty = React.useMemo(() => {
    const selected = normaliseRegionIds(selectedMarketRegionIds);

    const saved = normaliseRegionIds(savedMarketRegionIds);

    return selected.join(",") !== saved.join(",");
  }, [selectedMarketRegionIds, savedMarketRegionIds]);

  /* =====================================================
     TOGGLE MARKET REGION
     ===================================================== */

  function toggleMarketRegion(regionId) {
    if (marketRegionsSaving) {
      return;
    }

    const numericId = Number(regionId);

    setSelectedMarketRegionIds((previous) => {
      if (previous.includes(numericId)) {
        return previous.filter((id) => id !== numericId);
      }

      return [...previous, numericId];
    });
  }

  /* =====================================================
     SAVE MARKET REGIONS
     ===================================================== */

  async function saveMarketRegions() {
    const organisation = data?.organisation || {};

    if (
      !organisation.id ||
      marketRegionsSaving ||
      organisation.lifecycle_status === "ARCHIVED"
    ) {
      return;
    }

    try {
      setMarketRegionsSaving(true);

      setMarketRegionsError("");

      const token = await getAccessTokenSilently();

      const requestedIds = normaliseRegionIds(selectedMarketRegionIds);

      const response = await fetch(
        `${API_BASE}` +
          "/api/admin/" +
          "organisations/" +
          `${organisation.id}/` +
          "market-regions/",
        {
          method: "PATCH",

          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify({
            market_region_ids: requestedIds,
          }),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        let message = payload.detail;

        if (!message && typeof payload.market_region_ids === "string") {
          message = payload.market_region_ids;
        }

        if (!message && Array.isArray(payload.market_region_ids)) {
          message = payload.market_region_ids.join(" ");
        }

        throw new Error(
          message ||
            t(
              "marketRegions.saveError",
              "Could not update participating markets.",
            ),
        );
      }

      const savedIds = normaliseRegionIds(
        payload.market_region_ids || requestedIds,
      );

      setSelectedMarketRegionIds(savedIds);

      setSavedMarketRegionIds(savedIds);

      if (Array.isArray(payload.market_regions)) {
        setData((current) => ({
          ...current,

          organisation: {
            ...current.organisation,

            market_regions: payload.market_regions,
          },
        }));
      }

      showToast({
        type: "success",

        title: t("marketRegions.updatedTitle", "Participating markets updated"),

        message:
          payload.message ||
          t(
            "marketRegions.updatedMessage",
            "The lender's market participation has been updated successfully.",
          ),
      });
    } catch (err) {
      setMarketRegionsError(
        err.message ||
          t(
            "marketRegions.saveError",
            "Could not update participating markets.",
          ),
      );

      showToast({
        type: "error",

        title: t("marketRegions.saveErrorTitle", "Could not update markets"),

        message:
          err.message ||
          t(
            "marketRegions.saveError",
            "Could not update participating markets.",
          ),
      });
    } finally {
      setMarketRegionsSaving(false);
    }
  }

  /* =====================================================
     ARCHIVE MODAL
     ===================================================== */

  React.useEffect(() => {
    if (!archiveModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape" && !archiving) {
        setArchiveModalOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [archiveModalOpen, archiving]);

  React.useEffect(() => {
    if (!editModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape" && !editSaving) {
        setEditModalOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editModalOpen, editSaving]);

  React.useEffect(() => {
    return () => {
      if (postalSearchTimerRef.current) {
        window.clearTimeout(postalSearchTimerRef.current);
      }
    };
  }, []);

  async function saveOrganisationChanges() {
    if (editSaving || isArchived) {
      return;
    }

    const name = editForm.name.trim();

    const vatNumber = editForm.vat_number.trim();

    if (!name) {
      setEditError(t("edit.validation.name", "Organisation name is required."));

      return;
    }

    try {
      setEditSaving(true);

      setEditError("");

      const token = await getAccessTokenSilently();

      const requestBody = {
        name,
        vat_number: vatNumber,
      };

      if (isOss) {
        requestBody.coverage_postal_codes = editForm.postal_codes;
      }

      const response = await fetch(
        `${API_BASE}/api/admin/organisations/${organisation.id}/`,
        {
          method: "PATCH",

          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify(requestBody),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const fieldError =
          payload.name || payload.vat_number || payload.coverage_postal_codes;

        throw new Error(
          payload.detail ||
            (Array.isArray(fieldError) ? fieldError.join(" ") : fieldError) ||
            t("edit.saveError", "Could not update organisation."),
        );
      }

      showToast({
        type: "success",

        title: t("edit.successTitle", "Organisation updated"),

        message: t(
          "edit.successMessage",
          "Organisation information has been updated successfully.",
        ),
      });

      setEditModalOpen(false);

      await loadOrganisation();

      if (isLender) {
        await loadMarketRegions({
          silent: true,
        });
      }
    } catch (err) {
      setEditError(
        err.message || t("edit.saveError", "Could not update organisation."),
      );
    } finally {
      setEditSaving(false);
    }
  }

  /* =====================================================
     TOOLTIP
     ===================================================== */

  function hideUserTooltip() {
    setUserTooltip({
      visible: false,

      userId: null,

      x: 0,

      y: 0,
    });
  }

  function moveUserTooltip({ userId, x, y }) {
    setUserTooltip({
      visible: true,

      userId,

      x,

      y,
    });
  }

  function openUser(userId) {
    hideUserTooltip();

    navigate(`/admin/users/${userId}`);
  }

  /* =====================================================
     ARCHIVE
     ===================================================== */

  async function archiveOrganisation() {
    const organisation = data?.organisation || {};

    if (!organisation?.id || archiving) {
      return;
    }

    try {
      setArchiving(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}` +
          "/api/admin/" +
          "organisations/" +
          `${organisation.id}/archive/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",

            "Content-Type": "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.detail ||
            t("adminControls.archiveError", "Could not archive organisation."),
        );
      }

      setData((current) => {
        const currentUsers = current?.users || [];

        return {
          ...current,

          organisation: {
            ...current.organisation,

            ...payload.organisation,

            lifecycle_status: "ARCHIVED",

            is_active: false,
          },

          users: currentUsers.map((user) => ({
            ...user,

            is_active: false,

            account_is_active: false,

            assignment_status: "DISABLED",
          })),

          authentication: {
            ...current.authentication,

            inactive: currentUsers.length,
          },
        };
      });

      setArchiveModalOpen(false);

      showToast({
        type: "success",

        title: t("adminControls.archivedTitle", "Organisation archived"),

        message:
          payload.message ||
          t(
            "adminControls.archiveSuccess",
            "The organisation and its linked user accounts have been archived successfully.",
          ),
      });
    } catch (err) {
      showToast({
        type: "error",

        title: t("adminControls.archiveErrorTitle", "Archive failed"),

        message:
          err.message ||
          t("adminControls.archiveError", "Could not archive organisation."),
      });
    } finally {
      setArchiving(false);
    }
  }

  /* =====================================================
     LOADING
     ===================================================== */

  if (loading) {
    return (
      <main className={"admin-organisation-detail"}>
        <div className={"admin-organisation-detail-loading"}>
          <div className={"admin-organisation-detail-loading__back"} />

          <div className={"admin-organisation-detail-loading__hero"} />

          <div className={"admin-organisation-detail-loading__metrics"}>
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className={"admin-organisation-detail-loading__metric"}
              />
            ))}
          </div>

          <div className={"admin-organisation-detail-loading__grid"}>
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className={"admin-organisation-detail-loading__card"}
              />
            ))}
          </div>
        </div>
      </main>
    );
  }

  /* =====================================================
     ERROR
     ===================================================== */

  if (error) {
    return (
      <main className={"admin-organisation-detail"}>
        <button
          type="button"
          className={"admin-organisation-detail__back"}
          onClick={() => navigate("/admin/organisations")}
        >
          <ArrowLeft size={17} />

          {t("actions.back", "Organisations")}
        </button>

        <div className={"admin-organisation-detail-error"}>
          <Building2 size={30} />

          <strong>{t("errors.title", "Organisation unavailable")}</strong>

          <p>{error}</p>

          <button type="button" onClick={loadOrganisation}>
            <RefreshCw size={16} />

            {t("actions.retry", "Try again")}
          </button>
        </div>
      </main>
    );
  }

  /* =====================================================
     DATA
     ===================================================== */

  const organisation = data?.organisation || {};

  const summary = data?.summary || {};

  const authentication = data?.authentication || {};

  const users = data?.users || [];

  const lender = data?.lender || null;

  const oss = data?.oss || null;

  const recentActivity = data?.recent_activity || [];

  const isLender = organisation.org_type === "BANK";

  const isOss = organisation.org_type === "OSS";

  const isArchived = organisation.lifecycle_status === "ARCHIVED";

  const location = isLender
    ? formatCountry(organisation.country_code, t)
    : formatLocation(organisation, t);

  const initials = organisation.initials || getInitials(organisation.name);

  const affectedUserCount = summary.users ?? users.length;

  const ossProfile = oss?.profile || {};

  const lenderMarkets = organisation.market_regions || [];

  const ossOperatingModel =
    ossProfile.is_advisory && ossProfile.is_turnkey_renovation
      ? t("information.ossBoth", "Advisory & turnkey renovation")
      : ossProfile.is_advisory
        ? t("information.ossAdvisory", "Advisory")
        : ossProfile.is_turnkey_renovation
          ? t("information.ossTurnkey", "Turnkey renovation")
          : "";

  /* =====================================================
     METRICS
     ===================================================== */

  const metricCards = isLender
    ? [
        {
          icon: UsersRound,

          label: t("metrics.users", "Organisation users"),

          value: summary.users || 0,

          description: t(
            "metrics.usersDescription",
            "Active and inactive platform users",
          ),
        },

        {
          icon: FileText,

          label: t("metrics.loanRecords", "Loan records"),

          value: summary.records || 0,

          description: t(
            "metrics.loanRecordsDescription",
            "Loan cases associated with this lender",
          ),
        },

        {
          icon: CircleDollarSign,

          label: t("metrics.approvedAmount", "Approved amount"),

          value: summary.approved_amount || 0,

          currency: true,

          description: t(
            "metrics.approvedAmountDescription",
            "Final approved loan value",
          ),
        },
      ]
    : [
        {
          icon: UsersRound,

          label: t("metrics.users", "Organisation users"),

          value: summary.users || 0,

          description: t(
            "metrics.usersDescription",
            "Active and inactive platform users",
          ),
        },

        {
          icon: FolderKanban,

          label: t("metrics.projects", "Renovation projects"),

          value: summary.projects || 0,

          description: t(
            "metrics.projectsDescription",
            "Projects associated with this OSS",
          ),
        },

        {
          icon: CircleDollarSign,

          label: t("metrics.renovationValue", "Renovation value"),

          value: summary.renovation_value || 0,

          currency: true,

          description: t(
            "metrics.renovationValueDescription",
            "Estimated renovation project value",
          ),
        },
      ];

  function openEditOrganisation() {
    setEditError("");
    setPostalSearch("");
    setPostalSuggestions([]);
    setPostalSearchError("");

    postalSessionTokenRef.current = null;

    setEditForm({
      name: organisation.name || "",

      vat_number: organisation.vat_number || "",

      postal_codes: isOss
        ? (oss?.regions || [])
            .filter((region) => region?.postal_code)
            .map((region) => ({
              id: region.id || null,

              country_code: (
                region.country_code ||
                organisation.country_code ||
                ""
              ).toUpperCase(),

              postal_code: String(region.postal_code || "")
                .trim()
                .toUpperCase(),

              google_place_id: region.google_place_id || "",

              label: region.label || region.postal_code || "",
            }))
        : [],
    });

    setEditModalOpen(true);
  }

  async function getPostalSessionToken() {
    const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

    if (!google?.maps) {
      throw new Error(
        t("edit.coverage.mapsUnavailable", "Google Maps is unavailable."),
      );
    }

    const { AutocompleteSessionToken } =
      await google.maps.importLibrary("places");

    if (!postalSessionTokenRef.current) {
      postalSessionTokenRef.current = new AutocompleteSessionToken();
    }

    return postalSessionTokenRef.current;
  }

  async function runPostalSearch(input) {
    const query = input.trim();

    if (query.length < 2) {
      setPostalSuggestions([]);
      setPostalSearching(false);

      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      setPostalSearchError(
        t(
          "edit.coverage.mapsKeyMissing",
          "Google Maps is unavailable because the API key is not configured.",
        ),
      );

      return;
    }

    const countryCode = String(organisation.country_code || "")
      .trim()
      .toUpperCase();

    if (!countryCode) {
      setPostalSearchError(
        t(
          "edit.coverage.countryMissing",
          "The organisation does not have a country configured.",
        ),
      );

      return;
    }

    const requestId = ++postalSearchRequestRef.current;

    try {
      setPostalSearching(true);
      setPostalSearchError("");

      const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

      if (!google?.maps) {
        throw new Error("Google Maps is unavailable.");
      }

      const { AutocompleteSuggestion } =
        await google.maps.importLibrary("places");

      const sessionToken = await getPostalSessionToken();

      const { suggestions: results = [] } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,

          includedRegionCodes: [countryCode.toLowerCase()],

          includedPrimaryTypes: ["(regions)"],

          sessionToken,
        });

      if (requestId !== postalSearchRequestRef.current) {
        return;
      }

      const postalResults = results.filter((suggestion) =>
        suggestion.placePrediction?.types?.includes("postal_code"),
      );

      setPostalSuggestions(postalResults);
    } catch (error) {
      console.error("Postal code search failed:", error);

      if (requestId === postalSearchRequestRef.current) {
        setPostalSuggestions([]);

        setPostalSearchError(
          t(
            "edit.coverage.searchError",
            "Postal codes could not be loaded. Please try again.",
          ),
        );
      }
    } finally {
      if (requestId === postalSearchRequestRef.current) {
        setPostalSearching(false);
      }
    }
  }

  function handlePostalSearchChange(value) {
    setPostalSearch(value);
    setPostalSearchError("");

    if (postalSearchTimerRef.current) {
      window.clearTimeout(postalSearchTimerRef.current);
    }

    if (value.trim().length < 2) {
      postalSearchRequestRef.current += 1;

      setPostalSuggestions([]);
      setPostalSearching(false);

      return;
    }

    postalSearchTimerRef.current = window.setTimeout(() => {
      runPostalSearch(value);
    }, 250);
  }

  async function selectPostalSuggestion(suggestion) {
    try {
      setPostalSearchError("");

      const prediction = suggestion.placePrediction;

      if (!prediction) {
        return;
      }

      const place = prediction.toPlace();

      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });

      const postalComponent = place.addressComponents?.find((component) =>
        component.types.includes("postal_code"),
      );

      const countryComponent = place.addressComponents?.find((component) =>
        component.types.includes("country"),
      );

      const postalCode = String(postalComponent?.longText || "")
        .trim()
        .toUpperCase();

      const countryCode = String(countryComponent?.shortText || "")
        .trim()
        .toUpperCase();

      const organisationCountry = String(organisation.country_code || "")
        .trim()
        .toUpperCase();

      if (!postalCode) {
        setPostalSearchError(
          t(
            "edit.coverage.notPostalCode",
            "Please select a postal code rather than a city or region.",
          ),
        );

        return;
      }

      if (
        countryCode &&
        organisationCountry &&
        countryCode !== organisationCountry
      ) {
        setPostalSearchError(
          t(
            "edit.coverage.countryMismatch",
            "The selected postal code does not belong to this organisation's country.",
          ),
        );

        return;
      }

      const selectedCountry = countryCode || organisationCountry;

      setEditForm((current) => {
        const alreadyExists = current.postal_codes.some(
          (item) =>
            item.country_code === selectedCountry &&
            item.postal_code === postalCode,
        );

        if (alreadyExists) {
          return current;
        }

        return {
          ...current,

          postal_codes: [
            ...current.postal_codes,

            {
              country_code: selectedCountry,

              postal_code: postalCode,

              google_place_id: place.id || "",

              label:
                place.formattedAddress ||
                prediction.text?.toString() ||
                postalCode,
            },
          ],
        };
      });

      setPostalSearch("");
      setPostalSuggestions([]);

      postalSessionTokenRef.current = null;
    } catch (error) {
      console.error("Could not resolve postal code:", error);

      setPostalSearchError(
        t(
          "edit.coverage.selectionError",
          "The selected postal code could not be loaded.",
        ),
      );
    }
  }

  function removePostalCode(countryCode, postalCode) {
    setEditForm((current) => ({
      ...current,

      postal_codes: current.postal_codes.filter(
        (item) =>
          !(
            item.country_code === countryCode && item.postal_code === postalCode
          ),
      ),
    }));
  }
  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <main className={"admin-organisation-detail"}>
      {/* ===================================================
          BACK
          =================================================== */}

      <button
        type="button"
        className={"admin-organisation-detail__back"}
        onClick={() => navigate("/admin/organisations")}
      >
        <ArrowLeft size={17} />

        {t("actions.back", "Organisations")}
      </button>

      {/* ===================================================
          PAGE HEADER
          =================================================== */}

      <header className={"admin-organisation-detail__page-header"}>
        <h1>{t("header.title", "Organisation Profile")}</h1>

        <p>
          {t(
            "header.subtitle",
            "Detailed organisation information, associated users and platform activity.",
          )}
        </p>
      </header>

      {/* ===================================================
          HERO
          =================================================== */}

      <section className={"admin-organisation-detail-hero"}>
        <div
          className={`admin-organisation-detail-hero__logo ${
            organisation.logo_url
              ? "admin-organisation-detail-" + "hero__logo--image"
              : ""
          }`}
        >
          {organisation.logo_url ? (
            <img src={organisation.logo_url} alt="" />
          ) : (
            <span>{initials}</span>
          )}
        </div>

        <div className={"admin-organisation-detail-hero__content"}>
          <div className={"admin-organisation-detail-hero__title-line"}>
            <h2>{organisation.name}</h2>

            <span className={"admin-organisation-detail-type-badge"}>
              {isLender ? <Landmark size={13} /> : <Toolbox size={13} />}

              {formatOrganisationType(organisation.org_type, t)}
            </span>

            <span
              className={`admin-organisation-detail-status-badge ${
                isArchived
                  ? "is-archived"
                  : organisation.is_active === false
                    ? "is-inactive"
                    : "is-active"
              }`}
            >
              {isArchived
                ? t("account.archived", "Archived")
                : organisation.is_active === false
                  ? t("account.inactive", "Inactive")
                  : t("account.active", "Active")}
            </span>
          </div>

          {location ? (
            <div className={"admin-organisation-detail-hero__location"}>
              <MapPin size={14} />

              <span>{location}</span>
            </div>
          ) : null}

          <p>
            {isLender
              ? t(
                  "hero.lenderSummary",
                  "{{users}} users · {{records}} loan records",
                  {
                    users: summary.users || 0,

                    records: summary.records || 0,
                  },
                )
              : t(
                  "hero.ossSummary",
                  "{{users}} users · {{projects}} renovation projects",
                  {
                    users: summary.users || 0,

                    projects: summary.projects || 0,
                  },
                )}
          </p>
        </div>

        <div className={"admin-organisation-detail-hero__actions"}>
          <button
            type="button"
            className={
              "admin-organisation-detail-btn " +
              "admin-organisation-detail-btn--primary"
            }
            onClick={openEditOrganisation}
            disabled={isArchived}
          >
            <Pencil size={15} />

            {t("actions.edit", "Edit organisation")}
          </button>
        </div>
      </section>

      {/* ===================================================
          METRICS
          =================================================== */}

      <section className={"admin-organisation-detail-metrics"}>
        {metricCards.map((metric, index) => (
          <MetricCard
            key={metric.label}
            {...metric}
            locale={locale}
            delay={index * 70}
          />
        ))}
      </section>

      {/* ===================================================
          MAIN GRID
          =================================================== */}

      <section className={"admin-organisation-detail-grid"}>
        {/* =================================================
            INFORMATION
            ================================================= */}

        <article className={"admin-organisation-detail-card"}>
          <header className={"admin-organisation-detail-card__header"}>
            <div className={"admin-organisation-detail-card__title"}>
              <span className={"admin-organisation-detail-card__icon"}>
                <Building2 size={18} />
              </span>

              <div>
                <h2>{t("information.title", "Organisation Information")}</h2>

                <p>
                  {t(
                    "information.subtitle",
                    "Core organisation profile information.",
                  )}
                </p>
              </div>
            </div>
          </header>

          <div className={"admin-organisation-detail-card__body"}>
            <div className={"admin-organisation-detail-info"}>
              <InformationRow
                label={t("information.name", "Organisation name")}
                value={organisation.name}
              />

              <InformationRow
                label={t("information.type", "Organisation type")}
                value={formatOrganisationType(organisation.org_type, t)}
              />

              <InformationRow
                label={t("information.country", "Country")}
                value={formatCountry(organisation.country_code, t)}
              />

              <InformationRow
                label={t("information.vat", "VAT number")}
                value={organisation.vat_number}
              />

              <InformationRow
                label={t("information.status", "Lifecycle status")}
                value={
                  isArchived
                    ? t("account.archived", "Archived")
                    : organisation.is_active === false
                      ? t("account.inactive", "Inactive")
                      : t("account.active", "Active")
                }
              />

              <InformationRow
                label={t("information.created", "Registered")}
                value={formatDateTime(organisation.created_at, locale)}
              />

              <InformationRow
                label={t("information.updated", "Last updated")}
                value={formatDateTime(organisation.updated_at, locale)}
              />

              {/* LENDER */}

              {isLender ? (
                <>
                  <InformationRow
                    label={t("information.markets", "Participating markets")}
                    value={
                      lenderMarkets.length
                        ? lenderMarkets
                            .map((region) => region.name || region.code)
                            .filter(Boolean)
                            .join(", ")
                        : "—"
                    }
                  />

                  <InformationRow
                    label={t("information.legalDocument", "Legal document")}
                    value={
                      lender?.legal_document_url
                        ? t("information.available", "Available")
                        : t("information.notProvided", "Not provided")
                    }
                  />
                </>
              ) : null}

              {/* OSS */}

              {isOss ? (
                <>
                  <InformationRow
                    label={t("information.address", "Company address")}
                    value={ossProfile.company_address}
                  />

                  <InformationRow
                    label={t("information.contactEmail", "Contact email")}
                    value={ossProfile.contact_email}
                  />

                  <InformationRow
                    label={t("information.contactPhone", "Contact phone")}
                    value={ossProfile.contact_phone}
                  />

                  <InformationRow
                    label={t("information.website", "Website")}
                    value={ossProfile.website_url}
                  />

                  <InformationRow
                    label={t("information.experience", "Years of experience")}
                    value={ossProfile.years_of_experience ?? "—"}
                  />

                  <InformationRow
                    label={t("information.ossModel", "OSS model")}
                    value={ossOperatingModel}
                  />

                  <InformationRow
                    label={t("information.coverage", "Postal codes covered")}
                    value={oss?.regions?.length ?? 0}
                  />
                </>
              ) : null}
            </div>

            {isOss &&
            (ossProfile.short_description ||
              ossProfile.public_profile_summary) ? (
              <div className={"admin-organisation-detail-profile-summary"}>
                {ossProfile.short_description ? (
                  <div>
                    <span>
                      {t("information.description", "Organisation description")}
                    </span>

                    <p>{ossProfile.short_description}</p>
                  </div>
                ) : null}

                {ossProfile.public_profile_summary ? (
                  <div>
                    <span>
                      {t("information.publicSummary", "Public profile summary")}
                    </span>

                    <p>{ossProfile.public_profile_summary}</p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </article>

        {/* =================================================
            USERS
            ================================================= */}

        <article className={"admin-organisation-detail-card"}>
          <header className={"admin-organisation-detail-card__header"}>
            <div className={"admin-organisation-detail-card__title"}>
              <span className={"admin-organisation-detail-card__icon"}>
                <UsersRound size={18} />
              </span>

              <div>
                <h2>{t("users.title", "Organisation Users")}</h2>

                <p>
                  {t("users.subtitle", "{{count}} platform users", {
                    count: summary.users || users.length,
                  })}
                </p>
              </div>
            </div>
          </header>

          <div className={"admin-organisation-detail-card__body"}>
            {users.length > 0 ? (
              <div className={"admin-organisation-detail-users"}>
                {users.slice(0, 5).map((user) => (
                  <OrganisationUserRow
                    key={user.id}
                    user={user}
                    t={t}
                    onOpen={openUser}
                    onTooltipMove={moveUserTooltip}
                    onTooltipHide={hideUserTooltip}
                  />
                ))}

                {users.length > 5 ? (
                  <button
                    type="button"
                    className={"admin-organisation-detail-link"}
                    disabled
                  >
                    {t("users.viewAll", "View all users")}

                    <ChevronRight size={15} />
                  </button>
                ) : null}
              </div>
            ) : (
              <div className={"admin-organisation-detail-small-empty"}>
                <UsersRound size={22} />

                <strong>
                  {t("users.emptyTitle", "No organisation users")}
                </strong>

                <span>
                  {t(
                    "users.emptyText",
                    "No users are currently associated with this organisation.",
                  )}
                </span>
              </div>
            )}
          </div>
        </article>

        {/* =================================================
            DOMAIN ACTIVITY
            ================================================= */}

        <article className={"admin-organisation-detail-card"}>
          <header className={"admin-organisation-detail-card__header"}>
            <div className={"admin-organisation-detail-card__title"}>
              <span className={"admin-organisation-detail-card__icon"}>
                {isLender ? <Banknote size={18} /> : <FolderKanban size={18} />}
              </span>

              <div>
                <h2>
                  {isLender
                    ? t("lenderActivity.title", "Loan Activity")
                    : t("ossActivity.title", "Project Activity")}
                </h2>

                <p>
                  {isLender
                    ? t(
                        "lenderActivity.subtitle",
                        "Loan case activity associated with this lender.",
                      )
                    : t(
                        "ossActivity.subtitle",
                        "Renovation projects managed by this OSS.",
                      )}
                </p>
              </div>
            </div>
          </header>

          <div className={"admin-organisation-detail-card__body"}>
            {isLender ? (
              <div className={"admin-organisation-detail-stat-list"}>
                <InformationRow
                  label={t("lenderActivity.total", "Total loan records")}
                  value={lender?.total_records ?? summary.records ?? 0}
                />

                <InformationRow
                  label={t("lenderActivity.finalised", "Finalised")}
                  value={lender?.finalised ?? 0}
                />

                <InformationRow
                  label={t("lenderActivity.rejected", "Rejected")}
                  value={lender?.rejected ?? 0}
                />

                <InformationRow
                  label={t("lenderActivity.open", "Open / Draft")}
                  value={lender?.open ?? 0}
                />

                <InformationRow
                  label={t("lenderActivity.amount", "Approved amount")}
                  value={new Intl.NumberFormat(locale, {
                    style: "currency",

                    currency: "EUR",

                    notation: "compact",

                    maximumFractionDigits: 1,
                  }).format(
                    Number(
                      lender?.approved_amount ?? summary.approved_amount ?? 0,
                    ),
                  )}
                />
              </div>
            ) : (
              <div className={"admin-organisation-detail-stat-list"}>
                <InformationRow
                  label={t("ossActivity.total", "Total renovation projects")}
                  value={oss?.total_projects ?? summary.projects ?? 0}
                />

                <InformationRow
                  label={t("ossActivity.active", "Active projects")}
                  value={oss?.active_projects ?? 0}
                />

                <InformationRow
                  label={t("ossActivity.completed", "Completed projects")}
                  value={oss?.completed_projects ?? 0}
                />

                <InformationRow
                  label={t("ossActivity.other", "Other")}
                  value={oss?.other_projects ?? 0}
                />

                <InformationRow
                  label={t("ossActivity.value", "Renovation value")}
                  value={new Intl.NumberFormat(locale, {
                    style: "currency",

                    currency: "EUR",

                    notation: "compact",

                    maximumFractionDigits: 1,
                  }).format(
                    Number(
                      oss?.renovation_value ?? summary.renovation_value ?? 0,
                    ),
                  )}
                />
              </div>
            )}
          </div>
        </article>

        {/* =================================================
            AUTHENTICATION
            ================================================= */}

        <article className={"admin-organisation-detail-card"}>
          <header className={"admin-organisation-detail-card__header"}>
            <div className={"admin-organisation-detail-card__title"}>
              <span className={"admin-organisation-detail-card__icon"}>
                <ShieldCheck size={18} />
              </span>

              <div>
                <h2>{t("authentication.title", "Authentication Overview")}</h2>

                <p>
                  {t(
                    "authentication.subtitle",
                    "Authentication state of organisation users.",
                  )}
                </p>
              </div>
            </div>
          </header>

          <div className={"admin-organisation-detail-card__body"}>
            <div className={"admin-organisation-detail-auth-overview"}>
              <div>
                <span
                  className={
                    "admin-organisation-detail-" +
                    "auth-overview__icon is-verified"
                  }
                >
                  <BadgeCheck size={17} />
                </span>

                <span>
                  {t("authentication.verifiedUsers", "Verified users")}
                </span>

                <strong>{authentication.verified ?? 0}</strong>
              </div>

              <div>
                <span
                  className={
                    "admin-organisation-detail-" +
                    "auth-overview__icon is-unverified"
                  }
                >
                  <MailWarning size={17} />
                </span>

                <span>
                  {t("authentication.unverifiedUsers", "Unverified users")}
                </span>

                <strong>{authentication.unverified ?? 0}</strong>
              </div>

              <div>
                <span
                  className={
                    "admin-organisation-detail-" +
                    "auth-overview__icon is-local"
                  }
                >
                  <Database size={17} />
                </span>

                <span>
                  {t("authentication.localUsers", "Local-only users")}
                </span>

                <strong>{authentication.local_only ?? 0}</strong>
              </div>

              <div>
                <span
                  className={
                    "admin-organisation-detail-" +
                    "auth-overview__icon is-inactive"
                  }
                >
                  <CircleOff size={17} />
                </span>

                <span>
                  {t("authentication.inactiveUsers", "Inactive users")}
                </span>

                <strong>{authentication.inactive ?? 0}</strong>
              </div>
            </div>
          </div>
        </article>

        {/* =================================================
            LENDER PARTICIPATING MARKETS
            ================================================= */}

        {isLender ? (
          <article
            className={
              "admin-organisation-detail-card " +
              "admin-organisation-detail-card--wide " +
              "admin-organisation-detail-market-regions"
            }
          >
            <header className={"admin-organisation-detail-card__header"}>
              <div className={"admin-organisation-detail-card__title"}>
                <span className={"admin-organisation-detail-card__icon"}>
                  <Globe2 size={18} />
                </span>

                <div>
                  <h2>{t("marketRegions.title", "Participating Markets")}</h2>

                  <p>
                    {t(
                      "marketRegions.subtitle",
                      "Choose the LEG-UP market regions in which this lender participates.",
                    )}
                  </p>
                </div>
              </div>
            </header>

            <div className={"admin-organisation-detail-card__body"}>
              {marketRegionsLoading ? (
                <div
                  className={
                    "admin-organisation-detail-market-regions__loading"
                  }
                >
                  <Loader2
                    size={20}
                    className={
                      "admin-organisation-detail-market-regions__spinner"
                    }
                  />

                  <span>
                    {t("marketRegions.loading", "Loading market regions...")}
                  </span>
                </div>
              ) : marketRegionsError && availableMarketRegions.length === 0 ? (
                <div
                  className={"admin-organisation-detail-market-regions__error"}
                >
                  <Globe2 size={22} />

                  <div>
                    <strong>
                      {t(
                        "marketRegions.errorTitle",
                        "Market regions unavailable",
                      )}
                    </strong>

                    <span>{marketRegionsError}</span>
                  </div>

                  <button type="button" onClick={() => loadMarketRegions()}>
                    <RefreshCw size={15} />

                    {t("actions.retry", "Try again")}
                  </button>
                </div>
              ) : availableMarketRegions.length > 0 ? (
                <>
                  <div
                    className={
                      "admin-organisation-detail-market-regions__summary"
                    }
                  >
                    <div>
                      <strong>
                        {t("marketRegions.selected", "{{count}} selected", {
                          count: selectedMarketRegionIds.length,
                        })}
                      </strong>

                      <span>
                        {t(
                          "marketRegions.countryHint",
                          "Only active market regions matching the lender's country are available.",
                        )}
                      </span>
                    </div>

                    {isArchived ? (
                      <span
                        className={
                          "admin-organisation-detail-market-regions__archived"
                        }
                      >
                        <Archive size={14} />

                        {t("marketRegions.archived", "Archived organisation")}
                      </span>
                    ) : null}
                  </div>

                  <div
                    className={"admin-organisation-detail-market-regions__grid"}
                  >
                    {availableMarketRegions.map((region) => {
                      const selected = selectedMarketRegionIds.includes(
                        Number(region.id),
                      );

                      return (
                        <button
                          key={region.id}
                          type="button"
                          className={`admin-organisation-detail-market-region ${
                            selected ? "is-selected" : ""
                          }`}
                          aria-pressed={selected}
                          disabled={isArchived || marketRegionsSaving}
                          onClick={() => toggleMarketRegion(region.id)}
                        >
                          <span
                            className={
                              "admin-organisation-detail-market-region__check"
                            }
                          >
                            {selected ? (
                              <Check size={15} strokeWidth={3} />
                            ) : null}
                          </span>

                          <span
                            className={
                              "admin-organisation-detail-market-region__content"
                            }
                          >
                            <strong>{region.name}</strong>

                            <small>
                              {region.code}

                              {region.country_code ? (
                                <>
                                  {" · "}

                                  {formatCountry(region.country_code, t)}
                                </>
                              ) : null}
                            </small>

                            {region.formatted_address ? (
                              <span>
                                <MapPin size={13} />

                                {region.formatted_address}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {marketRegionsError ? (
                    <div
                      className={
                        "admin-organisation-detail-market-regions__inline-error"
                      }
                    >
                      <AlertTriangle size={15} />

                      <span>{marketRegionsError}</span>
                    </div>
                  ) : null}

                  <div
                    className={
                      "admin-organisation-detail-market-regions__actions"
                    }
                  >
                    <div>
                      {marketRegionsDirty ? (
                        <span
                          className={
                            "admin-organisation-detail-market-regions__unsaved"
                          }
                        >
                          {t(
                            "marketRegions.unsaved",
                            "You have unsaved changes.",
                          )}
                        </span>
                      ) : (
                        <span
                          className={
                            "admin-organisation-detail-market-regions__saved"
                          }
                        >
                          <Check size={14} />

                          {t(
                            "marketRegions.saved",
                            "Market assignments are up to date.",
                          )}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className={
                        "admin-organisation-detail-market-regions__save"
                      }
                      onClick={saveMarketRegions}
                      disabled={
                        isArchived || marketRegionsSaving || !marketRegionsDirty
                      }
                    >
                      {marketRegionsSaving ? (
                        <Loader2
                          size={16}
                          className={
                            "admin-organisation-detail-market-regions__spinner"
                          }
                        />
                      ) : (
                        <Check size={16} />
                      )}

                      {marketRegionsSaving
                        ? t("marketRegions.saving", "Saving...")
                        : t("marketRegions.save", "Save market regions")}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className={"admin-organisation-detail-market-regions__empty"}
                >
                  <Globe2 size={25} />

                  <strong>
                    {t(
                      "marketRegions.emptyTitle",
                      "No market regions available",
                    )}
                  </strong>

                  <span>
                    {t(
                      "marketRegions.emptyText",
                      "Create an active market region for this lender's country before assigning market participation.",
                    )}
                  </span>
                </div>
              )}
            </div>
          </article>
        ) : null}

        {/* =================================================
            OSS SERVICES / COVERAGE
            ================================================= */}

        {isOss ? (
          <article
            className={
              "admin-organisation-detail-card " +
              "admin-organisation-detail-card--wide"
            }
          >
            <header className={"admin-organisation-detail-card__header"}>
              <div className={"admin-organisation-detail-card__title"}>
                <span className={"admin-organisation-detail-card__icon"}>
                  <Wrench size={18} />
                </span>

                <div>
                  <h2>{t("services.title", "Services & Coverage")}</h2>

                  <p>
                    {t(
                      "services.subtitle",
                      "OSS services and geographic coverage.",
                    )}
                  </p>
                </div>
              </div>
            </header>

            <div className={"admin-organisation-detail-card__body"}>
              <div className={"admin-organisation-detail-services-grid"}>
                <div>
                  <h3>
                    <Toolbox size={16} />

                    {t("services.servicesLabel", "Services")}
                  </h3>

                  {oss?.services?.length ? (
                    <div className={"admin-organisation-detail-chip-list"}>
                      {oss.services.map((service, index) => (
                        <span key={service.id ?? service.name ?? index}>
                          {service.name || service.label || service}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className={"admin-organisation-detail-muted"}>
                      {t("services.noServices", "No services recorded.")}
                    </p>
                  )}
                </div>

                <div>
                  <h3>
                    <Globe2 size={16} />

                    {t("services.coverageLabel", "Regions covered")}
                  </h3>

                  {oss?.regions?.length ? (
                    <div className={"admin-organisation-detail-chip-list"}>
                      {oss.regions.slice(0, 8).map((region, index) => (
                        <span key={region.id ?? region.postal_code ?? index}>
                          <MapPin size={12} />

                          {region.postal_code}

                          {region.country_code
                            ? ` · ${region.country_code}`
                            : ""}
                        </span>
                      ))}

                      {oss.regions.length > 8 ? (
                        <span className={"is-more"}>
                          +{oss.regions.length - 8} {t("services.more", "more")}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p className={"admin-organisation-detail-muted"}>
                      {t("services.noRegions", "No region coverage recorded.")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </article>
        ) : null}
      </section>

      {/* ===================================================
          RECENT ACTIVITY
          =================================================== */}

      <RecentActivity items={recentActivity} locale={locale} t={t} />

      {/* ===================================================
          ADMIN CONTROLS
          =================================================== */}

      <section
        className={
          "admin-organisation-detail-card " +
          "admin-organisation-detail-card--wide " +
          "admin-organisation-detail-card--admin"
        }
      >
        <header className={"admin-organisation-detail-card__header"}>
          <div className={"admin-organisation-detail-card__title"}>
            <span className={"admin-organisation-detail-card__icon"}>
              <ShieldCheck size={18} />
            </span>

            <div>
              <h2>{t("adminControls.title", "Admin Controls")}</h2>

              <p>
                {t(
                  "adminControls.subtitle",
                  "Organisation lifecycle and access management.",
                )}
              </p>
            </div>
          </div>
        </header>

        <div className={"admin-organisation-detail-card__body"}>
          <div className={"admin-organisation-detail-admin-actions"}>
            <div className={"admin-organisation-detail-admin-action"}>
              <div>
                <span
                  className={"admin-organisation-detail-admin-action__icon"}
                >
                  <Archive size={18} />
                </span>

                <div>
                  <strong>
                    {isArchived
                      ? t(
                          "adminControls.archivedTitle",
                          "Organisation archived",
                        )
                      : t("adminControls.archiveTitle", "Archive organisation")}
                  </strong>

                  <p>
                    {isArchived
                      ? t(
                          "adminControls.archivedText",
                          "This organisation no longer participates in LEG-UP. Its historical data and account references remain preserved.",
                        )
                      : t(
                          "adminControls.archiveText",
                          "Remove this organisation from active LEG-UP participation and deactivate all linked user accounts while preserving historical platform data.",
                        )}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className={
                  "admin-organisation-detail-admin-action__archive-btn"
                }
                onClick={() => setArchiveModalOpen(true)}
                disabled={archiving || isArchived}
              >
                {isArchived
                  ? t("adminControls.archived", "Organisation archived")
                  : t("adminControls.archive", "Archive organisation")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================
          USER TOOLTIP
          =================================================== */}

      {userTooltip.visible
        ? createPortal(
            <span
              className={"admin-organisation-detail-user__floating-hint"}
              style={{
                left: userTooltip.x + 14,

                top: userTooltip.y + 14,
              }}
            >
              {t("users.openProfile", "Open user profile")}
            </span>,

            document.body,
          )
        : null}

      {/* ===================================================
          ARCHIVE MODAL
          =================================================== */}

      {archiveModalOpen
        ? createPortal(
            <div
              className={"admin-organisation-archive-modal__backdrop"}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !archiving) {
                  setArchiveModalOpen(false);
                }
              }}
            >
              <section
                className={"admin-organisation-archive-modal"}
                role="dialog"
                aria-modal="true"
                aria-labelledby={"archive-organisation-title"}
                aria-describedby={"archive-organisation-description"}
              >
                <button
                  type="button"
                  className={"admin-organisation-archive-modal__close"}
                  onClick={() => setArchiveModalOpen(false)}
                  disabled={archiving}
                  aria-label={t("common.close", "Close")}
                >
                  <X size={18} />
                </button>

                <div className={"admin-organisation-archive-modal__warning"}>
                  <AlertTriangle size={24} />
                </div>

                <div className={"admin-organisation-archive-modal__heading"}>
                  <h2 id={"archive-organisation-title"}>
                    {t("adminControls.modal.title", "Archive organisation?")}
                  </h2>

                  <p id={"archive-organisation-description"}>
                    {t(
                      "adminControls.modal.subtitle",
                      "This action removes the organisation from active LEG-UP participation.",
                    )}
                  </p>
                </div>

                <div
                  className={"admin-organisation-archive-modal__organisation"}
                >
                  <div
                    className={
                      "admin-organisation-archive-modal__organisation-logo"
                    }
                  >
                    {organisation.logo_url ? (
                      <img src={organisation.logo_url} alt="" />
                    ) : (
                      <span>{initials}</span>
                    )}
                  </div>

                  <div>
                    <strong>{organisation.name}</strong>

                    <span>
                      {formatOrganisationType(organisation.org_type, t)}
                    </span>
                  </div>
                </div>

                <div className={"admin-organisation-archive-modal__impact"}>
                  <div>
                    <span
                      className={
                        "admin-organisation-archive-modal__impact-icon"
                      }
                    >
                      <UsersRound size={17} />
                    </span>

                    <div>
                      <strong>
                        {t(
                          "adminControls.modal.usersTitle",
                          "{{count}} linked user accounts will be deactivated",
                          {
                            count: affectedUserCount,
                          },
                        )}
                      </strong>

                      <p>
                        {t(
                          "adminControls.modal.usersText",
                          "These users will no longer be able to access LEG-UP through this organisation.",
                        )}
                      </p>
                    </div>
                  </div>

                  <div>
                    <span
                      className={
                        "admin-organisation-archive-modal__" +
                        "impact-icon is-safe"
                      }
                    >
                      <ShieldCheck size={17} />
                    </span>

                    <div>
                      <strong>
                        {t(
                          "adminControls.modal.historyTitle",
                          "Historical data will be preserved",
                        )}
                      </strong>

                      <p>
                        {t(
                          "adminControls.modal.historyText",
                          "Loan cases, renovation projects, documents, messages and historical user references will not be deleted.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={"admin-organisation-archive-modal__notice"}>
                  <AlertTriangle size={16} />

                  <span>
                    {t(
                      "adminControls.modal.notice",
                      "Archive the organisation only when it is no longer participating in LEG-UP.",
                    )}
                  </span>
                </div>

                <div className={"admin-organisation-archive-modal__actions"}>
                  <button
                    type="button"
                    className={"admin-organisation-archive-modal__cancel"}
                    onClick={() => setArchiveModalOpen(false)}
                    disabled={archiving}
                  >
                    {t("actions.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className={"admin-organisation-archive-modal__confirm"}
                    onClick={archiveOrganisation}
                    disabled={archiving}
                  >
                    {archiving ? (
                      <>
                        <Loader2
                          size={16}
                          className={
                            "admin-organisation-archive-modal__spinner"
                          }
                        />

                        {t("adminControls.modal.archiving", "Archiving...")}
                      </>
                    ) : (
                      <>
                        <Archive size={16} />

                        {t("adminControls.archive", "Archive organisation")}
                      </>
                    )}
                  </button>
                </div>
              </section>
            </div>,

            document.body,
          )
        : null}
      {editModalOpen
        ? createPortal(
            <div
              className={"admin-organisation-edit-modal__backdrop"}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !editSaving) {
                  setEditModalOpen(false);
                }
              }}
            >
              <section
                className={"admin-organisation-edit-modal"}
                role="dialog"
                aria-modal="true"
                aria-labelledby={"edit-organisation-title"}
              >
                <button
                  type="button"
                  className={"admin-organisation-edit-modal__close"}
                  onClick={() => setEditModalOpen(false)}
                  disabled={editSaving}
                  aria-label={t("common.close", "Close")}
                >
                  <X size={18} />
                </button>

                <div className={"admin-organisation-edit-modal__icon"}>
                  {isLender ? <Landmark size={22} /> : <Toolbox size={22} />}
                </div>

                <div className={"admin-organisation-edit-modal__heading"}>
                  <h2 id={"edit-organisation-title"}>
                    {t("edit.title", "Edit organisation")}
                  </h2>

                  <p>
                    {isLender
                      ? t(
                          "edit.lenderSubtitle",
                          "Update the lender's core organisation information. Participating markets are managed separately.",
                        )
                      : t(
                          "edit.ossSubtitle",
                          "Update the OSS core organisation information. Services and geographic coverage are managed separately.",
                        )}
                  </p>
                </div>

                <div className={"admin-organisation-edit-modal__type"}>
                  {isLender ? <Landmark size={15} /> : <Toolbox size={15} />}

                  <span>
                    {formatOrganisationType(organisation.org_type, t)}
                  </span>
                </div>

                <div className={"admin-organisation-edit-modal__form"}>
                  {/* NAME */}

                  <label>
                    <span>{t("edit.fields.name", "Organisation name")}</span>

                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      disabled={editSaving}
                    />
                  </label>

                  {/* VAT */}

                  <label>
                    <span>{t("edit.fields.vat", "VAT number")}</span>

                    <input
                      type="text"
                      value={editForm.vat_number}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          vat_number: event.target.value,
                        }))
                      }
                      disabled={editSaving}
                    />
                  </label>

                  {/* COUNTRY — READ ONLY */}

                  <label>
                    <span>{t("edit.fields.country", "Country")}</span>

                    <input
                      type="text"
                      value={formatCountry(organisation.country_code, t)}
                      disabled
                    />

                    <small>
                      {t(
                        "edit.fields.countryReadOnly",
                        "Country is part of the organisation's registration context and cannot be changed here.",
                      )}
                    </small>
                  </label>

                  {/* TYPE — READ ONLY */}

                  <label>
                    <span>{t("edit.fields.type", "Organisation type")}</span>

                    <input
                      type="text"
                      value={formatOrganisationType(organisation.org_type, t)}
                      disabled
                    />
                  </label>

                  {/* OSS COVERAGE */}

                  {isOss ? (
                    <div className={"admin-organisation-edit-modal__coverage"}>
                      <div
                        className={
                          "admin-organisation-edit-modal__coverage-heading"
                        }
                      >
                        <div>
                          <span>
                            {t("edit.fields.coverage", "Postal code coverage")}
                          </span>

                          <small>
                            {t(
                              "edit.fields.coverageHint",
                              "Search Google Places and select the postal codes served by this OSS.",
                            )}
                          </small>
                        </div>

                        <strong>{editForm.postal_codes.length}</strong>
                      </div>

                      {/* GOOGLE SEARCH */}

                      <div
                        className={
                          "admin-organisation-edit-modal__postal-search"
                        }
                      >
                        <MapPin size={17} aria-hidden="true" />

                        <input
                          type="text"
                          value={postalSearch}
                          autoComplete="off"
                          placeholder={t(
                            "edit.fields.postalPlaceholder",
                            "Search postal code",
                          )}
                          disabled={editSaving}
                          onChange={(event) =>
                            handlePostalSearchChange(event.target.value)
                          }
                        />

                        {postalSearching ? (
                          <Loader2
                            size={16}
                            className={
                              "admin-organisation-edit-modal__postal-spinner"
                            }
                          />
                        ) : null}
                      </div>

                      {/* SEARCH RESULTS */}

                      {postalSuggestions.length > 0 ? (
                        <div
                          className={
                            "admin-organisation-edit-modal__postal-suggestions"
                          }
                          role="listbox"
                        >
                          {postalSuggestions.map((suggestion, index) => {
                            const prediction = suggestion.placePrediction;

                            return (
                              <button
                                key={prediction?.placeId || index}
                                type="button"
                                role="option"
                                disabled={editSaving}
                                onClick={() =>
                                  selectPostalSuggestion(suggestion)
                                }
                              >
                                <span
                                  className={
                                    "admin-organisation-edit-modal__postal-suggestion-icon"
                                  }
                                >
                                  <MapPin size={14} />
                                </span>

                                <span>{prediction?.text?.toString()}</span>

                                <ChevronRight size={14} />
                              </button>
                            );
                          })}
                        </div>
                      ) : null}

                      {postalSearchError ? (
                        <div
                          className={
                            "admin-organisation-edit-modal__postal-error"
                          }
                        >
                          <AlertTriangle size={14} />

                          <span>{postalSearchError}</span>
                        </div>
                      ) : null}

                      {/* SELECTED */}

                      {editForm.postal_codes.length > 0 ? (
                        <div
                          className={
                            "admin-organisation-edit-modal__postal-list"
                          }
                        >
                          {editForm.postal_codes.map((item) => (
                            <span
                              key={
                                `${item.country_code}-` + `${item.postal_code}`
                              }
                              title={item.label || item.postal_code}
                            >
                              <MapPin size={12} />

                              <strong>{item.postal_code}</strong>

                              <small>{item.country_code}</small>

                              <button
                                type="button"
                                disabled={editSaving}
                                onClick={() =>
                                  removePostalCode(
                                    item.country_code,
                                    item.postal_code,
                                  )
                                }
                                aria-label={t(
                                  "edit.fields.removePostalCode",
                                  "Remove postal code",
                                )}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div
                          className={
                            "admin-organisation-edit-modal__coverage-empty"
                          }
                        >
                          {t(
                            "edit.fields.noCoverage",
                            "No postal codes currently selected.",
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {editError ? (
                  <div className={"admin-organisation-edit-modal__error"}>
                    <AlertTriangle size={15} />

                    <span>{editError}</span>
                  </div>
                ) : null}

                <div className={"admin-organisation-edit-modal__scope"}>
                  <ShieldCheck size={16} />

                  <span>
                    {isLender
                      ? t(
                          "edit.lenderScope",
                          "Loan cases, users and participating market assignments are not changed by this action.",
                        )
                      : t(
                          "edit.ossScope",
                          "Projects, users, services and region coverage are not changed by this action.",
                        )}
                  </span>
                </div>

                <div className={"admin-organisation-edit-modal__actions"}>
                  <button
                    type="button"
                    className={"admin-organisation-edit-modal__cancel"}
                    onClick={() => setEditModalOpen(false)}
                    disabled={editSaving}
                  >
                    {t("actions.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className={"admin-organisation-edit-modal__save"}
                    onClick={saveOrganisationChanges}
                    disabled={editSaving}
                  >
                    {editSaving ? (
                      <>
                        <Loader2
                          size={16}
                          className={"admin-organisation-edit-modal__spinner"}
                        />

                        {t("edit.saving", "Saving...")}
                      </>
                    ) : (
                      <>
                        <Check size={16} />

                        {t("edit.save", "Save changes")}
                      </>
                    )}
                  </button>
                </div>
              </section>
            </div>,

            document.body,
          )
        : null}
    </main>
  );
}
